import { Link, useLocation } from "wouter";
import { CertiCoreMark } from "@/components/CertiCoreMark";
import {
  LayoutGrid, BookOpen, Users, UserPlus, ClipboardCheck, Wrench, Folder, BarChart3,
  Cpu, Settings, Home, Map, Paperclip, Inbox, Award, TrendingUp, Activity, Shield,
  AlertTriangle, RefreshCw, ListChecks, CalendarDays, ShieldCheck, FileText, History,
  Sparkles, type LucideIcon,
} from "lucide-react";
import { useDemoStore, roleLabels, type Role } from "@/store/useDemoStore";
import { useEffectiveUser } from "@/store/hooks";

interface NavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  matches?: (path: string) => boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navByRole: Record<Role, NavSection[]> = {
  admin: [
    {
      title: "Resumen",
      items: [
        { label: "Dashboard", icon: LayoutGrid, href: "/admin", matches: (p) => p === "/admin" },
      ],
    },
    {
      title: "Catálogo académico",
      items: [
        { label: "Estándares", icon: BookOpen, href: "/admin/estandares", matches: (p) => p.startsWith("/admin/estandares") && !p.includes("/reglas") && !p.includes("/instrumentos") },
        { label: "Rutas formativas", icon: Map, href: "/admin/rutas-formativas", matches: (p) => p.startsWith("/admin/rutas-formativas") },
        { label: "Reglas de dictamen", icon: ShieldCheck, href: "/admin/reglas", matches: (p) => p.includes("/reglas") || p === "/admin/reglas" },
        { label: "Instrumentos", icon: Wrench, href: "/admin/instrumentos", matches: (p) => p.includes("/instrumentos") || p === "/admin/instrumentos" },
      ],
    },
    {
      title: "Operación",
      items: [
        { label: "Grupos y generaciones", icon: Users, href: "/admin/grupos", matches: (p) => p.startsWith("/admin/grupos") },
        { label: "Calendario", icon: CalendarDays, href: "/admin/calendario" },
        { label: "Candidatos", icon: UserPlus, href: "/admin/candidatos" },
        { label: "Evaluaciones", icon: ClipboardCheck, href: "/admin/evaluaciones" },
        { label: "Expedientes", icon: Folder, href: "/admin/expedientes" },
      ],
    },
    {
      title: "Administración",
      items: [
        { label: "Usuarios y roles", icon: Users, href: "/admin/usuarios" },
        { label: "Reportes", icon: BarChart3, href: "/admin/reportes" },
        { label: "Asistente IA", icon: Sparkles, href: "/admin/ia" },
        { label: "Configuración", icon: Settings, href: "/admin/configuracion" },
      ],
    },
  ],
  alumno: [
    {
      title: "Mi avance",
      items: [
        { label: "Inicio", icon: Home, href: "/alumno", matches: (p) => p === "/alumno" },
        { label: "Mi ruta formativa", icon: Map, href: "/alumno/ruta" },
      ],
    },
    {
      title: "Evaluaciones",
      items: [
        { label: "Diagnóstico", icon: ClipboardCheck, href: "/alumno/diagnostico", matches: (p) => p.startsWith("/alumno/diagnostico") },
        { label: "Examen teórico", icon: ListChecks, href: "/alumno/examen-teorico", matches: (p) => p.startsWith("/alumno/examen") },
        { label: "Mis evidencias", icon: Paperclip, href: "/alumno/evidencias" },
      ],
    },
    {
      title: "Mi cuenta",
      items: [
        { label: "Mi expediente", icon: Folder, href: "/alumno/expediente" },
        { label: "Mi historial", icon: Award, href: "/alumno/historial" },
        { label: "Asistente IA", icon: Cpu, href: "/alumno/ia" },
      ],
    },
  ],
  evaluador: [
    {
      title: "Resumen",
      items: [
        { label: "Dashboard", icon: LayoutGrid, href: "/evaluador", matches: (p) => p === "/evaluador" },
      ],
    },
    {
      title: "Trabajo del día",
      items: [
        { label: "Evaluaciones asignadas", icon: ClipboardCheck, href: "/evaluador/evaluaciones", matches: (p) => p.startsWith("/evaluador/evaluacion") },
        { label: "Mis candidatos", icon: Users, href: "/evaluador/candidatos" },
        { label: "Instrumentos", icon: Wrench, href: "/evaluador/instrumentos" },
      ],
    },
    {
      title: "Apoyo",
      items: [
        { label: "Historial", icon: History, href: "/evaluador/historial" },
        { label: "Asistente IA", icon: Cpu, href: "/evaluador/ia" },
      ],
    },
  ],
  dictaminador: [
    {
      title: "Resumen",
      items: [
        { label: "Dashboard", icon: LayoutGrid, href: "/dictaminador", matches: (p) => p === "/dictaminador" },
      ],
    },
    {
      title: "Bandeja",
      items: [
        { label: "Pendientes de dictamen", icon: Inbox, href: "/dictaminador/bandeja", matches: (p) => p.startsWith("/dictaminador/expediente") || p === "/dictaminador/bandeja" },
        { label: "Expedientes", icon: Folder, href: "/dictaminador/expedientes" },
      ],
    },
    {
      title: "Resultados",
      items: [
        { label: "Certificados emitidos", icon: Award, href: "/dictaminador/historial", matches: (p) => p.startsWith("/dictaminador/certificado") || p === "/dictaminador/historial" },
        { label: "Asistente IA", icon: Cpu, href: "/dictaminador/ia" },
      ],
    },
  ],
  instructor: [
    {
      title: "Resumen",
      items: [
        { label: "Dashboard", icon: LayoutGrid, href: "/instructor", matches: (p) => p === "/instructor" },
      ],
    },
    {
      title: "Mis grupos",
      items: [
        { label: "Grupos asignados", icon: Users, href: "/instructor/grupos" },
        { label: "Avance de candidatos", icon: TrendingUp, href: "/instructor/avance" },
        { label: "Prácticas", icon: Activity, href: "/instructor/practicas" },
        { label: "Ruta formativa", icon: BookOpen, href: "/instructor/material" },
        { label: "Materiales", icon: FileText, href: "/instructor/materiales" },
      ],
    },
    {
      title: "Apoyo",
      items: [
        { label: "Asistente IA", icon: Cpu, href: "/instructor/ia" },
      ],
    },
  ],
  auditor: [
    {
      title: "Resumen",
      items: [
        { label: "Dashboard", icon: LayoutGrid, href: "/auditor", matches: (p) => p === "/auditor" },
      ],
    },
    {
      title: "Revisión",
      items: [
        { label: "Expedientes", icon: Folder, href: "/auditor/expedientes", matches: (p) => p.startsWith("/auditor/expediente") },
        { label: "Integridad", icon: Shield, href: "/auditor/integridad" },
        { label: "Inconsistencias", icon: AlertTriangle, href: "/auditor/inconsistencias" },
        { label: "Recertificación", icon: RefreshCw, href: "/auditor/recertificacion" },
      ],
    },
    {
      title: "Reportes",
      items: [
        { label: "Reportes de auditoría", icon: BarChart3, href: "/auditor/reportes" },
        { label: "Bitácora", icon: History, href: "/auditor/bitacora" },
        { label: "Agente IA auditor", icon: Cpu, href: "/auditor/ia" },
      ],
    },
  ],
};

export function Sidebar() {
  const role = useDemoStore((s) => s.activeRole);
  const setEffectiveUser = useDemoStore((s) => s.setEffectiveUser);
  const effectiveUser = useEffectiveUser();
  const [path] = useLocation();
  const sections = navByRole[role];

  return (
    <aside
      className="cc-scroll"
      style={{
        width: "var(--cc-sidebar-w)",
        background: "var(--cc-navy)",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "hidden",
      }}
      data-testid="sidebar"
    >
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          gap: 10,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <CertiCoreMark size={30} />
        </div>
        <div>
          <div style={{ color: "white", fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>Plataforma de certificación</div>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 9, fontWeight: 600, letterSpacing: "0.04em", lineHeight: 1.3, marginTop: 2 }}>
            DE ESTÁNDARES DE COMPETENCIAS
          </div>
        </div>
      </div>

      <div style={{ padding: "12px 16px 8px" }}>
        <div style={{ background: "rgba(255,255,255,0.07)", borderRadius: 8, padding: "8px 12px" }}>
          <div
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 2,
            }}
          >
            Perfil activo
          </div>
          <div style={{ color: "white", fontSize: 12, fontWeight: 600 }} data-testid="text-active-role">
            {roleLabels[role]}
          </div>
          {effectiveUser && (
            <div style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Ver como
                </div>
                <div
                  data-testid="text-effective-user"
                  style={{ color: "white", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={effectiveUser.nombre}
                >
                  {effectiveUser.nombre}
                </div>
              </div>
              <button
                data-testid="button-exit-view-as"
                onClick={() => setEffectiveUser(null)}
                title="Salir de 'Ver como'"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  border: "none",
                  color: "white",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "3px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Salir
              </button>
            </div>
          )}
        </div>
      </div>

      <nav className="cc-scroll" style={{ flex: 1, padding: "4px 8px 12px", overflow: "auto" }}>
        {sections.map((section) => (
          <div key={section.title} style={{ marginTop: 10 }}>
            <div
              style={{
                color: "rgba(255,255,255,0.38)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "4px 12px 6px",
              }}
            >
              {section.title}
            </div>
            {section.items.map((item) => {
              const isActive = item.matches ? item.matches(path) : path === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    borderRadius: 8,
                    marginBottom: 2,
                    background: isActive ? "rgba(14, 116, 144, 0.35)" : "transparent",
                    borderLeft: isActive ? "3px solid var(--cc-teal-light)" : "3px solid transparent",
                    cursor: "pointer",
                    textDecoration: "none",
                    transition: "background 0.15s",
                  }}
                >
                  <span style={{ color: isActive ? "white" : "rgba(255,255,255,0.5)", flexShrink: 0, display: "flex" }}>
                    <Icon size={15} />
                  </span>
                  <span
                    style={{
                      color: isActive ? "white" : "rgba(255,255,255,0.7)",
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 10, textAlign: "center" }}>
          Plataforma v1.0
        </div>
      </div>
    </aside>
  );
}
